import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Typography, Alert, Tag, Input, Select } from 'antd';
import WatercolorLoader from '../components/WatercolorLoader';
import { BookOutlined, PlusOutlined } from '@ant-design/icons';
import { attachBookToSeries, createSeries, listProjects, listSeries } from '../illustration/api';
import { getDefaultProvider } from '../illustration/providers/registry';

const { Title, Text, Paragraph } = Typography;

const HomePage = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [seriesList, setSeriesList] = useState([]);
  const [seriesTitle, setSeriesTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const provider = getDefaultProvider();

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, shelves] = await Promise.all([listProjects(), listSeries()]);
      setProjects(data.projects || []);
      setSeriesList(shelves.series || []);
    } catch (e) {
      setError(e.message || 'Could not load projects. Is the illustration API running?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    document.title = 'Illustration Studio';
  }, []);

  return (
    <div className="workflow-shell__content">
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <Title
          level={1}
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-display)',
            letterSpacing: 'var(--tracking-display)',
            margin: 0,
          }}
        >
          Illustration Studio
        </Title>
        <Text type="secondary">
          Provider: <Tag>{provider?.label || 'none'}</Tag>
          {provider?.defaultModel ? <Tag>{provider.defaultModel}</Tag> : null}
        </Text>
      </div>

      <Paragraph style={{ color: 'var(--color-text-secondary)', maxWidth: 'var(--measure-prose)' }}>
        Pick a project from the sidebar, or create a new book seeded by an Illustration Bible.
        Chapters carry treatments/prompts; pages are authored at 72 dpi and exported at 300 dpi.
      </Paragraph>

      <div style={{ marginBottom: 'var(--space-6)' }}>
        <Link to="/new">
          <Button type="primary" icon={<PlusOutlined />} size="large">
            Create New
          </Button>
        </Link>
      </div>

      {error && (
        <Alert type="error" showIcon title={error} style={{ marginBottom: 16 }} />
      )}

      <Title level={3} style={{ fontFamily: 'var(--font-display)', marginBottom: 'var(--space-3)' }}>
        Series
      </Title>
      <div style={{ display: 'flex', gap: 8, marginBottom: 'var(--space-4)', flexWrap: 'wrap' }}>
        <Input
          aria-label="Series title"
          placeholder="Series title"
          value={seriesTitle}
          onChange={(event) => setSeriesTitle(event.target.value)}
          style={{ maxWidth: 280 }}
        />
        <Button
          type="primary"
          disabled={!seriesTitle.trim()}
          onClick={async () => {
            try {
              const result = await createSeries(seriesTitle.trim());
              setSeriesTitle('');
              navigate(`/series/${result.series.id}`);
            } catch (err) {
              setError(err.message);
            }
          }}
        >
          New series
        </Button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginBottom: 'var(--space-6)' }}>
        {seriesList.map((series) => {
          const books = projects.filter((project) => project.seriesId === series.id);
          const freeBooks = projects.filter((project) => !project.seriesId);
          return (
            <div
              key={series.id}
              style={{
                padding: 'var(--space-4)',
                background: 'var(--color-bg-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-sm)',
              }}
            >
              <Link to={`/series/${series.id}`}>
                <Text strong>{series.title}</Text>
              </Link>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">
                  {books.length ? books.map((project) => project.title).join(', ') : 'No books yet'}
                </Text>
              </div>
              <Select
                style={{ minWidth: 240, marginTop: 8 }}
                placeholder="Add a book"
                value={undefined}
                onChange={async (projectId) => {
                  try {
                    await attachBookToSeries(series.id, projectId);
                    await refresh();
                  } catch (err) {
                    setError(err.message);
                  }
                }}
                options={freeBooks.map((project) => ({ value: project.id, label: project.title }))}
              />
            </div>
          );
        })}
        {!loading && !seriesList.length ? <Text type="secondary">No series yet.</Text> : null}
      </div>

      <Title level={3} style={{ fontFamily: 'var(--font-display)', marginBottom: 'var(--space-3)' }}>
        Projects
      </Title>

      {loading ? (
        <div className="watercolor-loader-slot">
          <WatercolorLoader />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {projects.map((p) => (
            <Link
              key={p.id}
              to={`/projects/${p.id}/chapters`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-4)',
                padding: 'var(--space-4)',
                background: 'var(--color-bg-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-sm)',
              }}
            >
              <BookOutlined style={{ fontSize: 22, color: 'var(--color-accent-text)' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text strong style={{ display: 'block' }}>{p.title}</Text>
                <Text type="secondary" style={{ fontSize: 'var(--text-caption)' }}>
                  Bible v{p.bibleVersion} · {p.status}
                  {p.resolution ? ` · ${p.resolution.workDpi}/${p.resolution.exportDpi} dpi` : ''}
                </Text>
              </div>
            </Link>
          ))}
          {!projects.length && (
            <Text type="secondary">No projects yet — use Create New to import a bible.</Text>
          )}
        </div>
      )}
    </div>
  );
};

export default HomePage;
